import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import vivuLogo from "/lovable-uploads/7e1871c0-8e57-4c24-97d7-5433160c25ea.png";

export const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const {
    toast
  } = useToast();
  const {
    signIn,
    signUp,
    resetPasswordRequest,
    updatePassword,
    isPasswordRecovery,
    logout,
    resendConfirmationEmail
  } = useAuth();
  const resetting = isResettingPassword || isPasswordRecovery;

  // Check if user is in password reset flow (from email link)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    console.log('URL hash params:', {
      type,
      fullHash: window.location.hash
    });
    if (type === 'recovery') {
      console.log('Setting password reset mode');
      setIsResettingPassword(true);
    }
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isForgotPassword) {
        // Forgot password flow
        if (!email) {
          toast({
            title: "Fehler",
            description: "Bitte geben Sie Ihre E-Mail-Adresse ein.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
        const {
          error
        } = await resetPasswordRequest(email);
        if (error) {
          // Check for unconfirmed email error
          const errorMessage = error.message?.toLowerCase() || '';
          if (errorMessage.includes('invalid') || errorMessage.includes('email address')) {
            toast({
              title: "E-Mail nicht bestätigt",
              description: "Ihre E-Mail-Adresse wurde noch nicht bestätigt. Bitte überprüfen Sie Ihren Posteingang oder fordern Sie eine neue Bestätigungs-E-Mail an.",
              variant: "destructive"
            });
            setShowResendOption(true);
          } else {
            toast({
              title: "Fehler",
              description: error.message,
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "E-Mail versendet",
            description: "Bitte überprüfen Sie Ihre E-Mail für den Passwort-Reset-Link."
          });
          setIsForgotPassword(false);
          setShowResendOption(false);
        }
      } else if (resetting) {
        // Reset password flow (from email link)
        if (!newPassword || !confirmPassword) {
          toast({
            title: "Fehler",
            description: "Bitte füllen Sie beide Passwortfelder aus.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          toast({
            title: "Fehler",
            description: "Die Passwörter stimmen nicht überein.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
        if (newPassword.length < 6) {
          toast({
            title: "Fehler",
            description: "Das Passwort muss mindestens 6 Zeichen lang sein.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
        const {
          error
        } = await updatePassword(newPassword);
        if (error) {
          toast({
            title: "Fehler",
            description: error.message,
            variant: "destructive"
          });
        } else {
          // Clear all reset states
          try {
            sessionStorage.removeItem('supabase_recovery');
          } catch (e) {}
          setIsResettingPassword(false);
          setNewPassword("");
          setConfirmPassword("");

          // Logout to force fresh login
          await logout();

          // Clear hash from URL
          window.history.replaceState(null, "", window.location.pathname);

          // Show success message
          toast({
            title: "Passwort geändert",
            description: "Ihr Passwort wurde erfolgreich geändert. Bitte melden Sie sich mit Ihrem neuen Passwort an.",
            className: "border-chart-5 bg-chart-5/10 text-foreground"
          });
        }
      } else {
        // Login/Signup flow
        if (!email || !password || isSignUp && !inviteCode) {
          toast({
            title: "Fehler",
            description: "Bitte füllen Sie alle Felder aus.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
        if (isSignUp) {
          const {
            error
          } = await signUp(email, password, inviteCode);
          if (error) {
            toast({
              title: "Registrierung fehlgeschlagen",
              description: error.message,
              variant: "destructive"
            });
          } else {
            toast({
              title: "Registrierung erfolgreich",
              description: "Bitte überprüfen Sie Ihre E-Mail zur Bestätigung."
            });
          }
        } else {
          const {
            error
          } = await signIn(email, password);
          if (error) {
            toast({
              title: "Anmeldung fehlgeschlagen",
              description: error.message,
              variant: "destructive"
            });
          } else {
            toast({
              title: "Anmeldung erfolgreich",
              description: "Willkommen im VIVU CRM!"
            });
          }
        }
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-primary">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold flex items-center justify-center gap-2">
            <img src={vivuLogo} alt="VIVU Logo" className="w-8 h-8 rounded-full object-cover" />
            {resetting ? "Passwort zurücksetzen" : isForgotPassword ? "Passwort vergessen" : isSignUp ? "Registrierung" : "VIVU CRM"}
          </CardTitle>
          <CardDescription>
            {resetting ? "Geben Sie Ihr neues Passwort ein" : isForgotPassword ? "Geben Sie Ihre E-Mail-Adresse ein" : isSignUp ? "Erstellen Sie ein neues Konto" : "Melden Sie sich an, um fortzufahren"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {resetting ? <>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Neues Passwort</Label>
                  <div className="relative">
                    <Input id="newPassword" type={showNewPassword ? "text" : "password"} placeholder="Neues Passwort eingeben" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="pr-10" />
                    <Button type="button" variant="ghost" size="sm" className="absolute inset-y-0 right-0 px-3 py-0 h-full hover:bg-transparent" onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                  <div className="relative">
                    <Input id="confirmPassword" type={showConfirmPassword ? "text" : "password"} placeholder="Passwort erneut eingeben" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="pr-10" />
                    <Button type="button" variant="ghost" size="sm" className="absolute inset-y-0 right-0 px-3 py-0 h-full hover:bg-transparent" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </div>
              </> : <>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" type="email" placeholder="E-Mail eingeben" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                {!isForgotPassword && <div className="space-y-2">
                    <Label htmlFor="password">Passwort</Label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? "text" : "password"} placeholder="Passwort eingeben" value={password} onChange={e => setPassword(e.target.value)} required className="pr-10" />
                      <Button type="button" variant="ghost" size="sm" className="absolute inset-y-0 right-0 px-3 py-0 h-full hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                  </div>}
                {isSignUp && <div className="space-y-2">
                    <Label htmlFor="inviteCode">Einladungscode</Label>
                    <Input id="inviteCode" type="text" placeholder="Einladungscode eingeben" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} required maxLength={10} />
                  </div>}
              </>}
            
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={isLoading}>
              {isLoading ? "Wird geladen..." : resetting ? "Passwort ändern" : isForgotPassword ? "Reset-Link senden" : isSignUp ? "Registrieren" : "Anmelden"}
            </Button>
          </form>
          
          <div className="text-center space-y-2">
            {!resetting && !isForgotPassword && <Button type="button" variant="link" onClick={() => setIsForgotPassword(true)} className="text-sm text-muted-foreground hover:text-foreground">
                Passwort vergessen?
              </Button>}
            
            {isForgotPassword && <>
              <Button type="button" variant="ghost" onClick={() => {
                setIsForgotPassword(false);
                setShowResendOption(false);
              }} className="text-sm text-muted-foreground hover:text-foreground">
                Zurück zum Login
              </Button>
              
              {showResendOption && email && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    E-Mail nicht erhalten?
                  </p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    disabled={isResendingEmail}
                    onClick={async () => {
                      setIsResendingEmail(true);
                      const { error } = await resendConfirmationEmail(email);
                      setIsResendingEmail(false);
                      if (error) {
                        toast({
                          title: "Fehler",
                          description: error.message,
                          variant: "destructive"
                        });
                      } else {
                        toast({
                          title: "E-Mail versendet",
                          description: "Eine neue Bestätigungs-E-Mail wurde an " + email + " gesendet."
                        });
                      }
                    }}
                    className="gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${isResendingEmail ? 'animate-spin' : ''}`} />
                    Bestätigungs-E-Mail erneut senden
                  </Button>
                </div>
              )}
            </>}
            
            {!resetting && !isForgotPassword && <Button type="button" variant="ghost" onClick={() => setIsSignUp(!isSignUp)} className="text-sm text-muted-foreground hover:text-foreground">
                {isSignUp ? "Haben Sie bereits ein Konto? Anmelden" : "Kein Konto? Registrieren"}
              </Button>}
          </div>
        </CardContent>
      </Card>
      
      <div className="mt-8 text-center">
        <p className="text-white text-sm">Vivu Finanz- und Versicherungsagentur GmbH © 2025</p>
      </div>
    </div>;
};